import { ApiClient, IApiClient } from "@iconics/core";
import { Authentication } from "./Core/Authentication";
import { ICache, Cache } from "@iconics/core";
import { IMsalProvider } from "./Providers.Msal/IMsalProvider";
import { MsalProvider } from "./Providers.Msal/MsalProvider";
import { IAuth0Provider } from "./Providers.Auth0/IAuth0Provider";
import { Auth0Provider } from "./Providers.Auth0/Auth0Provider";
import { ISessionStorageProvider, SessionStorageProvider } from "./Providers.SessionStorage/SessionStorageProvider";
import { ILocalStorageProvider, LocalStorageProvider } from "./Providers.LocalStorage/LocalStorageProvider";
import { Event } from "@iconics/core";
import { Component } from "react";
import Labels from "./Data.Labels/Labels";
import ApiMessages from "./Data.Labels/ApiMessages";
import { EmServiceWebEntryApiClientV1, IEmServiceWebEntryApiClientV1 } from "@iconics/providers.data-entry-api-client-v1";
import { IAppSettings, ISettingsProvider, SettingsProvider } from "./Providers.Settings/SettingsProvider";
import { InMemoryCache } from "@iconics/core";
import { FadxPlatformApiClientV2, IFadxPlatformApiClientV2 } from "@iconics/providers.fadx-platform-api-client-v2";
import { LanguageOptions, Localisation } from "@iconics/localisation";
import { IStaticDataProvider, StaticDataProvider } from "./Providers.StaticData/StaticDataProvider";
import { Components, ScreenSize, ThemeId } from "@iconics/components";
import { Core } from "@iconics/core";
import { PublicClientApplication } from "@azure/msal-browser";

let instance: (ApplicationContext | null) = null;
export function appContext(): ApplicationContext
{
    if (!instance)
    {
        instance = new ApplicationContext();
        instance.initialise();
    }
    return instance;
}

export class ApplicationContext
{
    public cache: ICache = {} as ICache;
    public inMemoryCache = {} as InMemoryCache;
    public components = new Components();
    public language!: LanguageOptions;
    public labels!: Labels;
    public apiMessages!: ApiMessages;
    public settings!: IAppSettings;
    public core!: Core;
    public localisation!: Localisation;

    // providers
    public apiClient = {} as IApiClient;
    public dataEntryApiClientV1 = {} as IEmServiceWebEntryApiClientV1;
    public fadxPlatformApiClientV2 = {} as IFadxPlatformApiClientV2;
    public msalProvider = {} as IMsalProvider;
    public auth0Provider = {} as IAuth0Provider;
    public sessionStorageProvider = {} as ISessionStorageProvider;
    public localStorageProvider = {} as ILocalStorageProvider;
    public settingsProvider = {} as ISettingsProvider;
    public staticDataProvider = {} as IStaticDataProvider;

    // helpers
    public state = {} as Readonly<AppState>;
    public authentication = {} as Authentication;

    public get tenantId() { return this.localStorageProvider.getTenantId(); }
    public set tenantId(value: string) { this.localStorageProvider.setTenantId(value); }

    public initialise(): void
    {
        this.state = new AppState();
        this.authentication = new Authentication();
        this.inMemoryCache = new InMemoryCache();
        this.inMemoryCache.startGarbageCollection();
    }

    public useDefaultCache(): void
    {
        this.cache = new Cache();
    }

    public useDefaultApiClient(): void
    {
        this.apiClient = new ApiClient(this.authentication, this.core);
        this.apiClient.initialise();
    }

    public useDefaultDataEntryApiClientV1(): void
    {
        const context = { baseUrl: this.settings.dataEntryApi };
        this.dataEntryApiClientV1 = new EmServiceWebEntryApiClientV1(context);
    }

    public useDefaultFadxPlatformApiClientV2(): void
    {
        const context = { baseUrl: this.settings.platformApi };
        this.fadxPlatformApiClientV2 = new FadxPlatformApiClientV2(context);
    }

    public useDefaultMsalProvider(): void
    {
        this.msalProvider = new MsalProvider();
    }

    public useDefaultAuth0Provider(): void
    {
        this.auth0Provider = new Auth0Provider();
    }

    public useDefaultLocalStorageProvider(): void
    {
        this.localStorageProvider = new LocalStorageProvider();
    }

    public useDefaultSessionStorageProvider(): void
    {
        this.sessionStorageProvider = new SessionStorageProvider();
    }

    public useDefaultSettingsProvider(): void
    {
        this.settingsProvider = new SettingsProvider();
    }

    public async initaliseSettings(): Promise<void>
    {
        this.settings = await this.settingsProvider.getSettings();
    }

    public useDefaultStaticDataProvider(labels: Labels): void
    {
        this.staticDataProvider = new StaticDataProvider(labels);
    }
}

export interface IAppState
{
    readonly isLoading: boolean;
    readonly pageTitle: string;
    readonly backUrl: string;
    readonly themeId: ThemeId;
    readonly numOfNewNotifications: number;
    readonly screenSize: ScreenSize;
    readonly showApiError: boolean;
    readonly apiErrorTitle: string;
    readonly apiErrorDetails: string;
    readonly apiErrorMessages: string[];
    readonly apiErrorStatus: number;
    readonly apiErrorInstance: string;
    readonly apiErrorTime: string;
    readonly msalClient?: PublicClientApplication;
}

export class AppState implements IAppState
{
    private get session() { return appContext().sessionStorageProvider; }

    public isLoading = false;
    public pageTitle = "";
    public backUrl = "";
    public themeId: ThemeId = 'konjo';
    public numOfNewNotifications = 0;
    public screenSize: ScreenSize = 'expanded';
    public showApiError = false;
    public apiErrorTitle = '';
    public apiErrorDetails = '';
    public apiErrorMessages = [];
    public apiErrorStatus = 0;
    public apiErrorInstance = '';
    public apiErrorTime = '';
    public msalClient = undefined;

    public changed = new Event<IPartialAppState>();

    public async set(state: IPartialAppState, sender?: Component): Promise<void>
    {
        for (const property in state)
        {
            (<any>this)[property] = (<any>state)[property];
        }
        await this.changed.notify(state, sender);
    }

    public subscribe(component: Component, handler: (state: IPartialAppState, sender?: Component) => Promise<void>, callHandlerImmediately: boolean = true): string
    {
        let ref = "";
        try
        {
            ref = this.changed.add((state, sender) => handler(state, sender as Component));
            if (callHandlerImmediately)
            {
                handler(this, component);
            }

            let originalComponentWillUnmount = component.componentWillUnmount;
            component.componentWillUnmount = () =>
            {
                this.unsubscribe(ref);
                if (originalComponentWillUnmount != null)
                {
                    originalComponentWillUnmount.bind(component)();
                }
            };
        }
        catch
        {
            this.unsubscribe(ref);
        }
        return ref;
    }

    public unsubscribe(ref: string): void
    {
        this.changed.remove(ref);
    }

    public autoMap<TComponentState>(component: Component<unknown, TComponentState>, map: (appState: IPartialAppState) => Partial<TComponentState>): string
    {
        // todo: would be better to insert this handler first so that the component's state is updated before other handlers are invoked
        return this.subscribe(component, async (appState, sender) =>
        {
            const componentState = map(appState);
            const componentStateWithoutUndefined: Partial<TComponentState> = {};

            for (const componentProperty in componentState)
            {
                if (componentState[componentProperty] != undefined)
                {
                    componentStateWithoutUndefined[componentProperty] = componentState[componentProperty];
                }
            }
            if (Object.keys(componentStateWithoutUndefined).length > 0)
            {
                component.setState(componentStateWithoutUndefined as Pick<TComponentState, keyof TComponentState>);
            }
        });
    }
}

export type IPartialAppState = Partial<IAppState>;
